import { objectiveOptions } from "@/app/training-wizard/training-wizard";

interface PersonalInfo {
  sex: "Hombre" | "Mujer";
  height: number;
  heightMetric: "cm" | "inch";
  neck: number;
  neckMetric: "cm" | "inch";
  waist: number;
  waistMetric: "cm" | "inch";
  hip?: number;
  hipMetric?: "cm" | "inch";
  weight: number;
  weightMetric: "kg" | "lb";
}

function getFatPercentage(
  personalInfo: PersonalInfo
): PersonalInfo & { fat_percentage: number } {
  let BF = 0;
  let heightM =
    personalInfo.heightMetric === "inch"
      ? personalInfo.height * 2.54
      : personalInfo.height;
  const neckM =
    personalInfo.neckMetric === "cm"
      ? personalInfo.neck
      : personalInfo.neck * 2.54;
  const waistM =
    personalInfo.waistMetric === "cm"
      ? personalInfo.waist
      : personalInfo.waist * 2.54;
  const hipM =
    personalInfo.hipMetric === "cm"
      ? (personalInfo.hip ?? 0)
      : (personalInfo.hip ?? 0) * 2.54;

  if (personalInfo.sex === "Hombre") {
    BF =
      495 /
        (1.0324 -
          0.19077 * Math.log10(waistM - neckM) +
          0.15456 * Math.log10(heightM)) -
      450;
  } else {
    BF =
      495 /
        (1.29579 -
          0.35004 * Math.log10(waistM + hipM - neckM) +
          0.221 * Math.log10(heightM)) -
      450;
  }

  return {
    ...personalInfo,
    fat_percentage: parseFloat(BF.toFixed(2)),
  };
}

export interface TrainingRecommendationData {
  sex: "Hombre" | "Mujer";
  height: number;
  heightMetric: "cm" | "inch";
  weight: number;
  weightMetric: "kg" | "lb";
  neck: number;
  neckMetric: "cm" | "inch";
  waist: number;
  waistMetric: "cm" | "inch";
  hip?: number;
  hipMetric?: "cm" | "inch";
}

export interface TrainingResult {
  IMC: number;
  IMM: number;
  BFF: number;
  IMCF: number;
  month: number;
  recommendation: string;
  recommendationId: string;
}

export function getTrainingRecommendation(
  data: TrainingRecommendationData
): TrainingResult {
  const updatedData = getFatPercentage(data);
  const {
    height,
    heightMetric,
    weight,
    weightMetric,
    sex,
    fat_percentage: BF,
  } = updatedData;

  let heightM = heightMetric === "cm" ? height / 100 : height * 0.0254;
  let weightKg = weightMetric === "lb" ? weight / 2.20462 : weight;

  const IMC = +(weightKg / Math.pow(heightM, 2)).toFixed(2);
  const IMM =
    sex === "Hombre"
      ? +(
          (weightKg * ((100 - BF) / 100 + 6.1 * (1.8 - heightM))) /
          Math.pow(heightM, 2)
        ).toFixed(2)
      : +((weightKg * ((100 - BF) / 100)) / Math.pow(heightM, 2)).toFixed(2);

  let result = "";
  let recommendationId = "";
  let BFF = BF;
  let IMCF = 0;
  let month = 0;

  switch (sex) {
    case "Hombre":
      if (IMC >= 30) {
        result = "Reducción del tejido graso";
        recommendationId = objectiveOptions[1].value;
      } else if (IMM < 18) {
        result = "Incremento de masa muscular";
        recommendationId = objectiveOptions[0].value;
      } else {
        result = "Entrenamiento saludable compensatorio";
        recommendationId = objectiveOptions[2].value;
      }
      break;
    case "Mujer":
      if (IMC >= 28) {
        result = "Reducción del tejido graso";
        recommendationId = objectiveOptions[1].value;
      } else if (IMM < 15) {
        result = "Incremento de masa muscular";
        recommendationId = objectiveOptions[0].value;
      } else {
        result = "Mejora la salud física y el bienestar";
        recommendationId = objectiveOptions[2].value;
      }
      break;
  }

  return {
    IMC,
    IMM,
    BFF,
    IMCF,
    month,
    recommendation: result,
    recommendationId,
  };
}
